/////////////////////////////////////////////////////////
// Configurations related to listing.                  //
// Main configuration here is the extended data config //
/////////////////////////////////////////////////////////

// The service and sector vocabularies are shared with the food-business form
// (configUser.js) rather than duplicated — see configServiceTaxonomy.js for
// why the registration document requires that.
import {
  serviceOptionsFor,
  FOOD_SECTORS,
  EMPLOYEE_BANDS,
  CLIENTS_SERVED_BANDS,
  EXPERIENCE_BANDS,
  SERVICE_DELIVERY_MODES,
} from './configServiceTaxonomy';

// Note: The listingFields come from listingFields asset nowadays by default.
//       To use this built-in configuration, you need to change the overwrite from configHelper.js
//       (E.g. use mergeDefaultTypesAndFieldsForDebugging func)

/**
 * Configuration options for listing fields (custom extended data fields):
 * - key:                           Unique key for the extended data field.
 * - scope (optional):              Scope of the extended data can be 'public', 'private', or 'metadata'.
 *                                  Default value: 'public'.
 *                                  Note: listing doesn't support 'protected' scope atm.
 * - schemaType (optional):         Schema for this extended data field.
 *                                  This is relevant when rendering components and querying listings.
 *                                  Possible values: 'enum', 'multi-enum', 'text', 'long', 'boolean'.
 * - enumOptions (optional):        Options shown for 'enum' and 'multi-enum' extended data.
 *                                  These are used to render options for inputs and filters on
 *                                  EditListingPage, ListingPage, and SearchPage.
 * - listingTypeConfig (optional):  Relationship configuration against listing types.
 *   - limitToListingTypeIds:         Indicator whether this listing field is relevant to a limited set of listing types.
 *   - listingTypeIds:                An array of listing types, for which this custom listing field is
 *                                    relevant and should be added. This is mandatory if limitToListingTypeIds is true.
 * - categoryConfig (optional):     Relationship configuration against categories.
 *   - limitToCategoryIds:            Indicator whether this listing field is relevant to a limited set of categories.
 *   - categoryIds:                   An array of categories, for which this custom listing field is
 *                                    relevant and should be added. This is mandatory if limitToCategoryIds is true.
 * - filterConfig:                  Filter configuration for listings query.
 *    - indexForSearch (optional):    If set as true, it is assumed that the extended data key has
 *                                    search index in place. I.e. the key can be used to filter
 *                                    listing queries (then scope needs to be 'public').
 *                                    Note: Sharetribe CLI can be used to set search index for the key:
 *                                    https://www.sharetribe.com/docs/references/extended-data/#search-schema
 *                                    Read more about filtering listings with public data keys from API Reference:
 *                                    https://www.sharetribe.com/api-reference/marketplace.html#extended-data-filtering
 *                                    Default value: false,
 *   - filterType:                    Sometimes a single schemaType can be rendered with different filter components.
 *                                    For 'enum' schema, filterType can be 'SelectSingleFilter' or 'SelectMultipleFilter'
 *   - label:                         Label for the filter, if the field can be used as query filter
 *   - searchMode (optional):         Search mode for indexed data with multi-enum schema.
 *                                    Possible values: 'has_all' or 'has_any'.
 *   - group:                         SearchPageWithMap has grouped filters. Possible values: 'primary' or 'secondary'.
 * - showConfig:                    Configuration for rendering listing. (How the field should be shown.)
 *   - label:                         Label for the saved data.
 *   - isDetail                       Can be used to hide detail row (of type enum, boolean, or long) from listing page.
 *                                    Default value: true,
 *   - unselectedOptions              For 'multi-enum' only. When true (the default), the profile renders
 *                                    EVERY option with the unselected ones struck through — 38 struck-out
 *                                    rows to show that a provider does two of them. Set false so the
 *                                    profile lists only what the provider actually selected.
 * - saveConfig:                    Configuration for adding and modifying extended data fields.
 *   - label:                         Label for the input field.
 *   - placeholderMessage (optional): Default message for user input.
 *   - isRequired (optional):         Is the field required for providers to fill
 *   - requiredMessage (optional):    Message for those fields, which are mandatory.
 */
export const listingFields = [
  ///////////////////////////////////////////////////////////////////////////
  // FORM 1 — SERVICE PROVIDER REGISTRATION                                //
  //                                                                       //
  // Source: docs/source-docs/pfs-registration-forms-2026-09.md, the form  //
  // the client re-sent on 2026-09-16 ("the onboarding form still does not //
  // align with the vetting documents Vivian shared").                     //
  //                                                                       //
  // That document REPLACES docs/source-docs/Service Provider Vetting      //
  // Form.txt, which is a different and older form. Everything here was    //
  // previously aligned to the old one — Sections A-J, a 24-option         //
  // organization type list, a flat 37-item `areasOfExpertise`, a five-    //
  // item compliance block — which is why repeated "align it" rounds never //
  // converged. Do not re-introduce a field from the old document.         //
  //                                                                       //
  // Where each section of Form 1 lives:                                   //
  //   §01 Contact Information ....... the USER (configUser.js): built-in  //
  //                                   name/email/phone + `jobTitle`.      //
  //   §02 About Your Organization ... `organization` panel below, plus    //
  //                                   the listing's own title (=          //
  //                                   organization name) and description  //
  //                                   (= the 100-150 word summary) on     //
  //                                   DETAILS.                            //
  //   §03 Services & Expertise ...... `services` panel — one field,       //
  //                                   `serviceAreas`.                     //
  //   §04 Food Industry Experience .. `industries` panel — `industriesServed`.
  //                                   Client feedback 2026-09-18, item 3:  //
  //                                   "This should be a separate section,  //
  //                                   similar to the form. It's easy for   //
  //                                   users to skip this if we merge it    //
  //                                   with the service section." Was       //
  //                                   `panel: 'services'` (shared with     //
  //                                   §03) until this round.               //
  //   §05 Geographic Coverage ....... `coverage` panel.                   //
  //   §06 Experience & Capacity ..... `experience` panel.                 //
  //   §07 Commercial Information .... `commercial` panel.                 //
  //   §08 Supporting Documents ...... configProfileDocuments.js, uploaded //
  //                                   on the DOCUMENTS tab (user-scoped   //
  //                                   privateFileAttachments, not listing //
  //                                   files — the `inquiry` listing type  //
  //                                   carries no `files` key at all).     //
  //   §09 Declaration & Consent ..... OnboardingPage/SubmitRegistrationForm.
  //                                                                       //
  // Two conventions carried over from the previous revision:              //
  //                                                                       //
  // 1. `showFilter: true` is the ONLY gate for rendering a filter and for //
  //    accepting a pub_<key> query param — isFilterEnabled in            //
  //    util/search.js ignores `indexForSearch` despite its own docstring. //
  //    Turning it on WITHOUT a live search schema does not error: the API //
  //    returns 200, silently ignores the unindexed key, and every listing //
  //    matches. So it is set only on keys whose schema is confirmed live  //
  //    (`country`, `serviceAreas`).                                       //
  //                                                                       //
  //    `serviceAreas` keeps its key precisely for that reason. Its option //
  //    list is replaced wholesale by the §03 taxonomy, but a Sharetribe   //
  //    search schema is set per KEY and TYPE, not per value — the key is  //
  //    still `multi-enum`/`public`, so the live pub_serviceAreas filter   //
  //    keeps working with no Console or flex-cli work. A new key would    //
  //    have needed set-schema first, and until then would have silently   //
  //    matched every listing.                                             //
  //                                                                       //
  // 2. A field that is not filterable carries NO `filterConfig` at all.   //
  //    `validFilterConfig` runs `validLabel` over the whole block and     //
  //    `validLabel(undefined)` is INVALID, so a bare                      //
  //    `filterConfig: { indexForSearch: false }` fails validation and     //
  //    validListingFields DROPS THE ENTIRE FIELD — with a console warning //
  //    only, so the field simply never appears in the wizard.             //
  //                                                                       //
  // To make the remaining §03-§07 fields filterable, run these first and  //
  // only then add `showFilter: true`:                                     //
  //   flex-cli search set-schema --key=organizationType  --type=multi-enum --scope=public -m testmarketplace817830-dev
  //   flex-cli search set-schema --key=industriesServed  --type=multi-enum --scope=public -m testmarketplace817830-dev
  //   flex-cli search set-schema --key=countriesServed   --type=multi-enum --scope=public -m testmarketplace817830-dev
  //   flex-cli search set-schema --key=serviceDelivery   --type=enum       --scope=public -m testmarketplace817830-dev
  //   flex-cli search set-schema --key=pricingModels     --type=multi-enum --scope=public -m testmarketplace817830-dev
  ///////////////////////////////////////////////////////////////////////////

  // === §02. About Your Organization ======================================
  // Organization name and the 100-150 word description are the listing's
  // built-in `title` and `description` on the DETAILS panel — they are not
  // custom fields, so they are labelled in en.json, not here.
  {
    key: 'country',
    panel: 'organization',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'Ethiopia', label: 'Ethiopia' },
      { option: 'Ghana', label: 'Ghana' },
      { option: 'Ivory Coast', label: 'Ivory Coast' },
      { option: 'Kenya', label: 'Kenya' },
      { option: 'Malawi', label: 'Malawi' },
      { option: 'Nigeria', label: 'Nigeria' },
      { option: 'Rwanda', label: 'Rwanda' },
      { option: 'Senegal', label: 'Senegal' },
      { option: 'South Africa', label: 'South Africa' },
      { option: 'Tanzania', label: 'Tanzania' },
      { option: 'Togo', label: 'Togo' },
      { option: 'Uganda', label: 'Uganda' },
      { option: 'Zambia', label: 'Zambia' },
      // Thirteen countries — the PFS programme geography — plus a catch-all.
      // Form 1 §02 does not enumerate countries, so this list is a product
      // decision rather than something the document contradicts.
      // 'Other' keeps a provider based outside it from being unable to
      // register; documentsForUser() has no country set for 'Other', so they
      // correctly get the general document set.
      //
      // SETTLED 2026-09-16 — do not re-open without the client asking. The
      // document describes the countries-served control as a "searchable
      // multi-select", which could be read as asking for a global list. Asked
      // which they wanted, the client chose: "I would say 13 country list."
      // The rejected alternative: swapping these thirteen for ~195 ISO
      // countries needs a searchable picker component (a 195-checkbox group is
      // unusable) and would orphan the live pub_country search index.
      //
      // Kept byte-identical to `countriesServed` below and to `country` in
      // configUser.js, order included, so the three stay interchangeable and
      // keep matching the `countries` arrays in configProfileDocuments.js.
      { option: 'Other', label: 'Other' },
    ],
    filterConfig: {
      indexForSearch: true,
      // Safe: the pub_country search schema is confirmed live.
      showFilter: true,
      filterType: 'SelectSingleFilter',
      label: 'Country',
      group: 'primary',
    },
    showConfig: {
      label: 'Headquarters country',
      isDetail: true,
    },
    saveConfig: {
      label: 'Headquarters country',
      placeholderMessage: 'Select a country…',
      isRequired: true,
      requiredMessage: 'Select the country your organization is headquartered in.',
    },
  },
  {
    key: 'establishedYear',
    panel: 'organization',
    scope: 'public',
    schemaType: 'long',
    numberConfig: {
      minimum: 1900,
      maximum: 2100,
    },
    showConfig: {
      label: 'Year established',
      isDetail: true,
    },
    saveConfig: {
      label: 'Year established',
      isRequired: false,
    },
  },
  {
    key: 'businessAddress',
    panel: 'organization',
    scope: 'public',
    schemaType: 'text',
    showConfig: {
      label: 'City / State / Region',
      isDetail: true,
    },
    saveConfig: {
      label: 'City / State / Region',
      placeholderMessage: 'e.g. Accra, Greater Accra Region',
      isRequired: false,
    },
  },
  {
    key: 'website',
    panel: 'organization',
    scope: 'public',
    schemaType: 'shortText',
    showConfig: {
      label: 'Website',
      isDetail: true,
    },
    saveConfig: {
      label: 'Website',
      placeholderMessage: 'https://…',
      isRequired: false,
    },
  },
  {
    key: 'organizationType',
    panel: 'organization',
    scope: 'public',
    schemaType: 'multi-enum',
    // Exactly the seven options of Form 1 §02, replacing the 24-option list
    // built from the superseded document. The old values (`consultant`,
    // `engineering-firm`, `packaging-supplier`, …) are not carried over:
    // several of them are now Services & Expertise leaves rather than
    // organization types, which is the structural change the client has been
    // asking for.
    enumOptions: [
      { option: 'consulting-professional-services', label: 'Consulting / Professional Services' },
      { option: 'equipment-technology-provider', label: 'Equipment / Technology Provider' },
      { option: 'financial-institution-investor', label: 'Financial Institution / Investor' },
      { option: 'laboratory-certification-body', label: 'Laboratory / Certification Body' },
      { option: 'supplier-distributor', label: 'Supplier / Distributor' },
      {
        option: 'nonprofit-development-organization',
        label: 'Nonprofit / Development Organization',
      },
      { option: 'other', label: 'Other' },
    ],
    showConfig: {
      label: 'Organization type',
      isDetail: true,
      unselectedOptions: false,
    },
    saveConfig: {
      label: 'Organization type',
      placeholderMessage: 'Select all that apply…',
      isRequired: true,
      requiredMessage: 'Select at least one organization type.',
    },
  },

  // === §03. Services & Expertise =========================================
  // ONE field, not one per heading. The document's own note — "The same
  // service taxonomy is used for provider capabilities and food-business
  // needs to enable accurate marketplace matching" — only holds if a
  // provider's answer and a food business's answer are the same shape, so
  // both sides store a single multi-enum drawn from configServiceTaxonomy.js.
  // The six headings are preserved by `group` on each option, which
  // FieldCheckboxGroup renders as sub-headings.
  {
    key: 'serviceAreas',
    panel: 'services',
    scope: 'public',
    schemaType: 'multi-enum',
    enumOptions: serviceOptionsFor('provider'),
    filterConfig: {
      indexForSearch: true,
      // Safe: pub_serviceAreas has live search schema, and the schema is set
      // per key and type — replacing the option list does not invalidate it.
      showFilter: true,
      filterType: 'SelectMultipleFilter',
      searchMode: 'has_any',
      label: 'Services',
      group: 'primary',
    },
    showConfig: {
      label: 'Services & expertise',
      isDetail: true,
      unselectedOptions: false,
    },
    saveConfig: {
      label: 'Which services do you provide?',
      placeholderMessage: 'Select all that apply…',
      // The document does not asterisk §03, but this is the field the whole
      // marketplace matches on — a profile with none of it cannot be matched
      // to anything, and the superseded config required its equivalent
      // (`areasOfExpertise`) for the same reason.
      isRequired: true,
      requiredMessage: 'Select at least one service you provide.',
    },
  },

  // === §04. Food Industry Experience =====================================
  {
    key: 'industriesServed',
    panel: 'industries',
    scope: 'public',
    schemaType: 'multi-enum',
    // Same sixteen leaves the food business picks in Form 2 §03 as its value
    // chains — shared from configServiceTaxonomy.js so the two sides stay
    // comparable.
    enumOptions: FOOD_SECTORS,
    showConfig: {
      label: 'Food sectors worked with',
      isDetail: true,
      unselectedOptions: false,
    },
    saveConfig: {
      label: 'Which food sectors have you worked with?',
      placeholderMessage: 'Select all that apply…',
      isRequired: false,
    },
  },

  // === §05. Geographic Coverage & Delivery ===============================
  {
    key: 'countriesServed',
    panel: 'coverage',
    scope: 'public',
    schemaType: 'multi-enum',
    // Kept in step with the `country` list above on purpose: a provider who
    // can name Zambia as its headquarters should be able to name it as a
    // country it serves. See the open question on `country`.
    enumOptions: [
      { option: 'Ethiopia', label: 'Ethiopia' },
      { option: 'Ghana', label: 'Ghana' },
      { option: 'Ivory Coast', label: 'Ivory Coast' },
      { option: 'Kenya', label: 'Kenya' },
      { option: 'Malawi', label: 'Malawi' },
      { option: 'Nigeria', label: 'Nigeria' },
      { option: 'Rwanda', label: 'Rwanda' },
      { option: 'Senegal', label: 'Senegal' },
      { option: 'South Africa', label: 'South Africa' },
      { option: 'Tanzania', label: 'Tanzania' },
      { option: 'Togo', label: 'Togo' },
      { option: 'Uganda', label: 'Uganda' },
      { option: 'Zambia', label: 'Zambia' },
      { option: 'Other', label: 'Other' },
    ],
    showConfig: {
      label: 'Countries served',
      isDetail: true,
      unselectedOptions: false,
    },
    saveConfig: {
      label: 'Countries where you can provide services',
      placeholderMessage: 'Select all that apply…',
      isRequired: true,
      requiredMessage: 'Select at least one country where you can provide services.',
    },
  },
  {
    key: 'serviceDelivery',
    panel: 'coverage',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: SERVICE_DELIVERY_MODES,
    showConfig: {
      label: 'Service delivery',
      isDetail: true,
    },
    saveConfig: {
      label: 'Service delivery',
      placeholderMessage: 'Select an option…',
      isRequired: false,
    },
  },

  // === §06. Experience & Capacity ========================================
  // The three capacity questions are banded dropdowns in the document, not
  // free numbers — the previous revision stored raw integers
  // (`numberOfConsultants`, `numberOfClients`, a numeric
  // `yearsOfExperience`), which is a different question from the one the
  // form asks and cannot be filtered into the document's buckets.
  {
    key: 'numberOfEmployees',
    panel: 'experience',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: EMPLOYEE_BANDS,
    showConfig: {
      label: 'Full-time employees',
      isDetail: true,
    },
    saveConfig: {
      label: 'Number of full-time employees',
      placeholderMessage: 'Select a range…',
      isRequired: false,
    },
  },
  {
    key: 'foodBusinessesServed',
    panel: 'experience',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: CLIENTS_SERVED_BANDS,
    showConfig: {
      label: 'Food businesses served',
      isDetail: true,
    },
    saveConfig: {
      label: 'Approximately how many food businesses have you served?',
      placeholderMessage: 'Select a range…',
      isRequired: false,
    },
  },
  {
    key: 'yearsOfExperience',
    panel: 'experience',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: EXPERIENCE_BANDS,
    showConfig: {
      label: 'Years of relevant experience',
      isDetail: true,
    },
    saveConfig: {
      label: 'Years of relevant experience',
      placeholderMessage: 'Select a range…',
      isRequired: true,
      requiredMessage: 'Select your years of relevant experience.',
    },
  },

  // --- §06. Relevant assignments -----------------------------------------
  // Three assignments of five fields each, exactly as the document tables
  // them. Sharetribe extended data has no repeatable group in this template,
  // so the repeat is flattened into numbered keys rather than collapsed into
  // three free-text boxes — the structure is the point (country and service
  // are what make an assignment checkable during vetting).
  //
  // All optional: the document asterisks nothing in this block, and a new
  // provider with no listed assignments must still be able to submit.
  ...[1, 2, 3].flatMap(n => [
    {
      key: `assignment${n}Client`,
      panel: 'experience',
      scope: 'public',
      schemaType: 'shortText',
      showConfig: { label: `Assignment ${n} — client / organization`, isDetail: true },
      saveConfig: {
        label: `Assignment ${n}: Client / Organization`,
        placeholderMessage: 'Client or organization name',
        isRequired: false,
      },
    },
    {
      key: `assignment${n}Country`,
      panel: 'experience',
      scope: 'public',
      schemaType: 'shortText',
      showConfig: { label: `Assignment ${n} — country`, isDetail: true },
      saveConfig: {
        label: `Assignment ${n}: Country`,
        placeholderMessage: 'Country',
        isRequired: false,
      },
    },
    {
      key: `assignment${n}Service`,
      panel: 'experience',
      scope: 'public',
      schemaType: 'shortText',
      showConfig: { label: `Assignment ${n} — service provided`, isDetail: true },
      saveConfig: {
        label: `Assignment ${n}: Service provided`,
        placeholderMessage: 'Service provided',
        isRequired: false,
      },
    },
    {
      key: `assignment${n}Year`,
      panel: 'experience',
      scope: 'public',
      schemaType: 'long',
      numberConfig: { minimum: 1900, maximum: 2100 },
      showConfig: { label: `Assignment ${n} — year`, isDetail: true },
      saveConfig: { label: `Assignment ${n}: Year`, isRequired: false },
    },
    {
      key: `assignment${n}Description`,
      panel: 'experience',
      scope: 'public',
      schemaType: 'text',
      showConfig: { label: `Assignment ${n} — description / results`, isDetail: true },
      saveConfig: {
        label: `Assignment ${n}: Brief description / results`,
        placeholderMessage: 'What was delivered, and what changed as a result?',
        isRequired: false,
      },
    },
  ]),

  // === §07. Commercial Information =======================================
  {
    key: 'pricingModels',
    panel: 'commercial',
    scope: 'public',
    schemaType: 'multi-enum',
    enumOptions: [
      { option: 'hourly', label: 'Hourly' },
      { option: 'daily', label: 'Daily' },
      { option: 'fixed-project-fee', label: 'Fixed project fee' },
      { option: 'retainer', label: 'Retainer' },
      { option: 'other', label: 'Other' },
    ],
    showConfig: {
      label: 'Charging model',
      isDetail: true,
      unselectedOptions: false,
    },
    saveConfig: {
      label: 'How do you typically charge for your services?',
      placeholderMessage: 'Select all that apply…',
      isRequired: false,
    },
  },
  // The fee range is `private` because the document says so in as many words:
  // "Pricing information should be treated as confidential and not displayed
  // publicly." Private listing data is readable by the author and by
  // operators, and never appears on the public listing page — which is what
  // that note asks for. The charging MODEL above stays public: it is how a
  // provider bills, not what they charge.
  //
  // Three fields rather than one free-text "USD 200-800/day" box, so the
  // numbers are comparable across providers during vetting.
  {
    key: 'feeCurrency',
    panel: 'commercial',
    scope: 'private',
    schemaType: 'shortText',
    showConfig: { label: 'Fee currency' },
    saveConfig: {
      label: 'Typical fee range — currency',
      placeholderMessage: 'e.g. USD',
      isRequired: false,
    },
  },
  {
    key: 'feeRangeFrom',
    panel: 'commercial',
    scope: 'private',
    schemaType: 'long',
    numberConfig: { minimum: 0, maximum: 100000000 },
    showConfig: { label: 'Fee range from' },
    saveConfig: { label: 'Typical fee range — from', isRequired: false },
  },
  {
    key: 'feeRangeTo',
    panel: 'commercial',
    scope: 'private',
    schemaType: 'long',
    numberConfig: { minimum: 0, maximum: 100000000 },
    showConfig: { label: 'Fee range to' },
    saveConfig: { label: 'Typical fee range — to', isRequired: false },
  },
  // {
  //   "scope": "public",
  //   "label": "Gears",
  //   "key": "gears",
  //   "schemaType": "long",
  //   "numberConfig": {
  //     "minimum": 1,
  //     "maximum": 24
  //   },
  //   "filterConfig": {
  //     "indexForSearch": true,
  //     "group": "primary",
  //     "label": "Gears"
  //   }
  // }
  // {
  //   key: 'bikeType',
  //   scope: 'public',
  //   schemaType: 'enum',
  //   enumOptions: [
  //     { option: 'city-bikes', label: 'City bikes' },
  //     { option: 'electric-bikes', label: 'Electric bikes' },
  //     { option: 'mountain-bikes', label: 'Mountain bikes' },
  //     { option: 'childrens-bikes', label: "Children's bikes" },
  //   ],
  //   categoryConfig: {
  //     limitToCategoryIds: true,
  //     categoryIds: ['cats'],
  //   },
  //   filterConfig: {
  //     showFilter: true,
  //     filterType: 'SelectMultipleFilter', //'SelectSingleFilter',
  //     label: 'Bike type',
  //     group: 'primary',
  //   },
  //   showConfig: {
  //     label: 'Bike type',
  //     isDetail: true,
  //   },
  //   saveConfig: {
  //     label: 'Bike type',
  //     placeholderMessage: 'Select an option…',
  //     isRequired: true,
  //     requiredMessage: 'You need to select a bike type.',
  //   },
  // },
  // {
  //   key: 'tire',
  //   scope: 'public',
  //   schemaType: 'enum',
  //   enumOptions: [
  //     { option: '29', label: '29' },
  //     { option: '28', label: '28' },
  //     { option: '27', label: '27' },
  //     { option: '26', label: '26' },
  //     { option: '24', label: '24' },
  //     { option: '20', label: '20' },
  //     { option: '18', label: '18' },
  //   ],
  //   filterConfig: {
  //     showFilter: true,
  //     label: 'Tire size',
  //     group: 'secondary',
  //   },
  //   showConfig: {
  //     label: 'Tire size',
  //     isDetail: true,
  //   },
  //   saveConfig: {
  //     label: 'Tire size',
  //     placeholderMessage: 'Select an option…',
  //     isRequired: true,
  //     requiredMessage: 'You need to select a tire size.',
  //   },
  // },
  // {
  //   key: 'brand',
  //   scope: 'public',
  //   schemaType: 'enum',
  //   enumOptions: [
  //     { option: 'cube', label: 'Cube' },
  //     { option: 'diamant', label: 'Diamant' },
  //     { option: 'ghost', label: 'GHOST' },
  //     { option: 'giant', label: 'Giant' },
  //     { option: 'kalkhoff', label: 'Kalkhoff' },
  //     { option: 'kona', label: 'Kona' },
  //     { option: 'otler', label: 'Otler' },
  //     { option: 'vermont', label: 'Vermont' },
  //   ],
  //   filterConfig: {
  //     showFilter: true,
  //     label: 'Brand',
  //     group: 'secondary',
  //   },
  //   showConfig: {
  //     label: 'Brand',
  //     isDetail: true,
  //   },
  //   saveConfig: {
  //     label: 'Brand',
  //     placeholderMessage: 'Select an option…',
  //     isRequired: true,
  //     requiredMessage: 'You need to select a brand.',
  //   },
  // },
  // {
  //   key: 'accessories',
  //   scope: 'public',
  //   schemaType: 'multi-enum',
  //   enumOptions: [
  //     { option: 'bell', label: 'Bell' },
  //     { option: 'lights', label: 'Lights' },
  //     { option: 'lock', label: 'Lock' },
  //     { option: 'mudguard', label: 'Mudguard' },
  //   ],
  //   filterConfig: {
  //     showFilter: true,
  //     label: 'Accessories',
  //     searchMode: 'has_all',
  //     group: 'secondary',
  //   },
  //   showConfig: {
  //     label: 'Accessories',
  //   },
  //   saveConfig: {
  //     label: 'Accessories',
  //     placeholderMessage: 'Select an option…',
  //     isRequired: false,
  //   },
  // },
  // // An example of how to use transaction type specific custom fields and private data.
  // {
  //   key: 'note',
  //   scope: 'public',
  //   schemaType: 'text',
  //   listingTypeConfig: {
  //     limitToListingTypeIds: true,
  //     listingTypeIds: ['product-selling'],
  //   },
  //   showConfig: {
  //     label: 'Extra notes',
  //   },
  //   saveConfig: {
  //     label: 'Extra notes',
  //     placeholderMessage: 'Some public extra note about this bike...',
  //   },
  // },
  // {
  //   key: 'privatenote',
  //   scope: 'private',
  //   schemaType: 'text',
  //   listingTypeConfig: {
  //     limitToListingTypeIds: true,
  //     listingTypeIds: ['daily-booking'],
  //   },
  //   saveConfig: {
  //     label: 'Private notes',
  //     placeholderMessage: 'Some private note about this bike...',
  //   },
  // },
];

///////////////////////////////////////////////////////////////////////
// Configurations related to listing types and transaction processes //
///////////////////////////////////////////////////////////////////////

// A presets of supported listing configurations
//
// Note 1: The listingTypes come from listingTypes asset nowadays by default.
//         To use this built-in configuration, you need to change the overwrite from configHelper.js
//         (E.g. use mergeDefaultTypesAndFieldsForDebugging func)
// Note 2: transaction type is part of listing type. It defines what transaction process and units
//         are used when transaction is created against a specific listing.

/**
 * Configuration options for listing experience:
 * - listingType:         Unique string. This will be saved to listing's public data on
 *                        EditListingWizard.
 * - label                Label for the listing type. Used as microcopy for options to select
 *                        listing type in EditListingWizard.
 * - transactionType      Set of configurations how this listing type will behave when transaction is
 *                        created.
 *   - process              Transaction process.
 *                          The process must match one of the processes that this client app can handle
 *                          (check src/util/transactions/transaction.js) and the process must also exists in correct
 *                          marketplace environment.
 *   - alias                Valid alias for the aforementioned process. This will be saved to listing's
 *                          public data as transctionProcessAlias and transaction is initiated with this.
 *   - unitType             Unit type is mainly used as pricing unit. This will be saved to
 *                          transaction's protected data.
 *                          Recommendation: don't use same unit types in completely different processes
 *                          ('item' sold should not be priced the same as 'item' booked).
 * - stockType            This is relevant only to listings using default-purchase process.
 *                        If set to 'oneItem', stock management is not showed and the listing is
 *                        considered unique (stock = 1).
 *                        Possible values: 'oneItem', 'multipleItems', 'infiniteOneItem', and 'infiniteMultipleItems'.
 *                        Default: 'multipleItems'.
 * - availabilityType     This is relevant only to listings using default-booking process.
 *                        If set to 'oneSeat', seat management is not showed and the listing is
 *                        considered per person (seat = 1).
 *                        Possible values: 'oneSeat' and 'multipleSeats'.
 *                        Default: 'oneSeat'.
 * - priceVariations      This is relevant only to listings using default-booking process.
 *   - enabled:             If set to true, price variations are enabled.
 *                          Default: false.
 * - defaultListingFields These are tied to transaction processes. Different processes have different flags.
 *                        E.g. default-inquiry can toggle price and location to true/false value to indicate,
 *                        whether price (or location) tab should be shown. If defaultListingFields.price is not
 *                        explicitly set to _false_, price will be shown.
 *                        If the location or pickup is not used, listing won't be returned with location search.
 *                        Use keyword search as main search type if location is not enforced.
 *                        The payoutDetails flag allows provider to bypass setting of payout details.
 *                        Note: customers can't order listings, if provider has not set payout details! Monitor
 *                        providers who have not set payout details and contact them to ensure that they add the details.
 * - transactionFields    You can define an array of custom transaction fields for each listing type. Each transaction field
 *                        should have the following attributes:
 *                        - key (string)
 *                        - label (string)
 *                        - showTo (string, options: 'customer', 'provider'). Option 'provider' is only used for negotiation process.
 *                        - schemaType (string, options: 'enum', 'multi-enum', 'text', 'long', 'boolean', 'youtubeVideoUrl')
 *                        - saveConfig (object, optional,  { required: true })
 *                        - schema specific attributes:
 *                          - numberConfig (object, for schemaType: 'long'): { minimum: number, maximum: number }
 *                          - enumOptions (array, for schemaType: 'enum', 'multi-enum'): [{ label: string, option: string }]
 * - messagingOptions     Options for the messaging experience
 *  - fileAttachments:    - if set to true, uploading file attachments to messages is enabled. Marketplace level access control
 *                          configuration may still disable uploading and downloading files, even if enabled in the listing type.
 */

export const listingTypes = [
  // // Here are some examples of listingTypes
  // // TODO: SearchPage does not work well if both booking and product selling are used at the same time
  // {
  //   listingType: 'daily-booking',
  //   label: 'Daily booking',
  //   transactionType: {
  //     process: 'default-booking',
  //     alias: 'default-booking/release-1',
  //     unitType: 'day',
  //   },
  //   availabilityType: 'oneSeat',
  //   defaultListingFields: {
  //     location: true,
  //     payoutDetails: true,
  //   },
  //   transactionFields: [
  //     {
  //       showTo: 'customer',
  //       label: 'Extra requests for the hosts',
  //       key: 'requests',
  //       schemaType: 'text',
  //     },
  //     {
  //       showTo: 'customer',
  //       label: 'Are you traveling with minors?',
  //       key: 'minors',
  //       schemaType: 'boolean',
  //     },
  //     {
  //       showTo: 'customer',
  //       numberConfig: {
  //         minimum: 1,
  //         maximum: 10,
  //       },
  //       label: 'How many people are staying at the venue',
  //       key: 'peopleStaying',
  //       schemaType: 'long',
  //       saveConfig: {
  //         required: true,
  //       },
  //     },
  //     {
  //       showTo: 'customer',
  //       enumOptions: [
  //         {
  //           label: 'Morning cleanup (10am-12am)',
  //           option: 'morning',
  //         },
  //         {
  //           label: 'Afternoon cleanup (2pm-4pm)',
  //           option: 'afternoon',
  //         },
  //       ],
  //       label: 'Schedule preference',
  //       key: 'schedulePreference',
  //       schemaType: 'enum',
  //     },
  //     {
  //       showTo: 'customer',
  //       enumOptions: [
  //         {
  //           label: 'Vegetarian',
  //           option: 'vegetarian',
  //         },
  //         {
  //           label: 'Vegan',
  //           option: 'vegan',
  //         },
  //         {
  //           label: 'Gluten free',
  //           option: 'glutenFree',
  //         },
  //         {
  //           label: 'No caffeine',
  //           option: 'decaf',
  //         },
  //         {
  //           label: 'Nut free',
  //           option: 'nutFree',
  //         },
  //         {
  //           label: 'Dairy free',
  //           option: 'dairyFree',
  //         },
  //       ],
  //       label: 'Dietary preferences',
  //       key: 'dietaryPreferences',
  //       schemaType: 'multi-enum',
  //     },
  //   ],
  //   messagingOptions	{ fileAttachments: false }
  // },
  // {
  //   listingType: 'nightly-booking',
  //   label: 'Nightly booking',
  //   transactionType: {
  //     process: 'default-booking',
  //     alias: 'default-booking/release-1',
  //     unitType: 'night',
  //   },
  // },
  // {
  //   listingType: 'hourly-booking',
  //   label: 'Hourly booking',
  //   transactionType: {
  //     process: 'default-booking',
  //     alias: 'default-booking/release-1',
  //     unitType: 'hour',
  //   },
  // },
  // {
  //   listingType: 'product-selling',
  //   label: 'Sell bicycles',
  //   transactionType: {
  //     process: 'default-purchase',
  //     alias: 'default-purchase/release-1',
  //     unitType: 'item',
  //   },
  //   stockType: 'multipleItems',
  //   defaultListingFields: {
  //     shipping: true,
  //     pickup: true,
  //     payoutDetails: true,
  //   },
  // },
  // {
  //   listingType: 'inquiry',
  //   label: 'Inquiry',
  //   transactionType: {
  //     process: 'default-inquiry',
  //     alias: 'default-inquiry/release-1',
  //     unitType: 'inquiry',
  //   },
  //   defaultListingFields: {
  //     price: false,
  //     location: true,
  //   },
  // },
  //   {
  //   label: 'Digital file upload',
  //   listingType: 'digital-file',
  //   transactionType: {
  //     process: 'default-download',
  //     alias: 'default-download/release-1',
  //     unitType: 'file',
  //   },
  //   transactionFields: [
  //     {
  //       label: 'Arbitrary field',
  //       key: 'arbitrary',
  //       schemaType: 'shortText',
  //       showTo: 'customer',
  //       helpText: 'A text field with a 70 char limit',
  //     },
  //   ],
  //   defaultListingFields: {
  //     description: true,
  //     availability: false,
  //     payoutDetails: true,
  //     images: false,
  //     pickup: false,
  //     title: true,
  //     shipping: false,
  //     location: false,
  //     price: true,
  //     stock: false,
  //   },
  // },
];

// SearchPage can enforce listing query to only those listings with valid listingType
// However, it only works if you have set 'enum' type search schema for the public data fields
//   - listingType
//
//  Similar setup could be expanded to 2 other extended data fields:
//   - transactionProcessAlias
//   - unitType
//
// Read More:
// https://www.sharetribe.com/docs/how-to/manage-search-schemas-with-flex-cli/#adding-listing-search-schemas
export const enforceValidListingType = false;
